import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hasPendingScrollRestore, isMemoryPath, makeRouteKey } from '@/lib/scrollMemory';
import { peekReturn } from '@/lib/navHistory';

type LenisLike = {
  scrollTo: (target: number, options?: { immediate?: boolean }) => void;
};

/** Scrolls to top on every route change */
export default function ScrollToTop() {
  const { pathname, search, key } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    // Only skip top-scroll for an intentional product-detail back action.
    // Normal pagination/search/category changes should always return to top.
    const routeKey = makeRouteKey(pathname, search);
    if (isMemoryPath(pathname) && hasPendingScrollRestore(routeKey)) return;

    // Generic PDP back: if we have a saved return entry pointing at this
    // URL, the list page will restore scroll itself — don't fight it.
    const back = peekReturn();
    if (back && back.url === routeKey) return;

    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      const lenis = (window as Window & { __lenis?: LenisLike }).__lenis;
      lenis?.scrollTo(0, { immediate: true });
    };

    scrollToTop();
    const raf = window.requestAnimationFrame(scrollToTop);
    const timeout = window.setTimeout(scrollToTop, 0);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
    };
  }, [pathname, search, key]);

  return null;
}
